import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getMissionByGuid, createMission, updateMission } from '../services/missionService';
import { v4 as uuidv4 } from 'uuid';
import { MenuItem, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import CONFIG from '../config/config';
import CustomerSection from './CustomerSection';
import PersonnelSection from './PersonnelSection';
import ScheduleSection from './ScheduleSection';
import PreflightSection from './PreflightSection';
import ShotListSection from './ShotListSection';
import PostflightSection from './PostflightSection';
import DetailsSection from './DetailsSection';
import AssetsSection from './AssetsSection';
import { reverseGeocode } from '../services/mapboxService';
import { useOrganization } from '../contexts/OrganizationContext';
import { getSubscriptionByGuid } from '../services/subscriptionService';
import { CAPABILITIES, hasCapability } from '../services/capabilitiesService';

import InputAdornment from '@mui/material/InputAdornment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ListAltIcon from '@mui/icons-material/ListAlt';
import SatelliteIcon from '@mui/icons-material/SatelliteAltOutlined';
import LocationIcon from '@mui/icons-material/AddLocationAltOutlined';
import RollFilmIcon from '@mui/icons-material/CameraRoll';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';
import IconButton from '@mui/material/IconButton';
import MyLocationIcon from '@mui/icons-material/MyLocation';

import {
    Button,
    Box,
    Typography,
    Collapse,
    TextField,
    LinearProgress,
} from '@mui/material';

mapboxgl.accessToken = CONFIG.MAPBOX.PUBLIC_TOKEN;

const MissionPlanner = () => {
    const { missionGuid } = useParams();
    const navigate = useNavigate();
    const [isLocationExpanded, setLocationExpanded] = useState(true);
    const [isGpsExpanded, setGpsExpanded] = useState(true);
    const [originalMissionData, setOriginalMissionData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isDirty, setIsDirty] = useState(false);
    const [modifiedFields, setModifiedFields] = useState({});
    const [map, setMap] = useState(null);
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const [fetchedAddress, setFetchedAddress] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [shouldCenterMap, setShouldCenterMap] = useState(false);
    const personnelRef = useRef(null); // Ref to access PersonnelSection validation
    const postflightRef = useRef(null); // Ref to access PostflightSection validation
    const { selectedOrg, updateSelectedOrg } = useOrganization();
    const [subscription, setSubscription] = useState(null);
    const [isDeterminingLocation, setIsDeterminingLocation] = useState(false);
    const defaultCenter = [-98.5795, 39.8283]; // Default to center of the US
    const defaultZoom = 4;
    const markerRef = useRef(null);
    const [isSubscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
    const [dialogMessage, setDialogMessage] = useState('');

    const [missionData, setMissionData] = useState({
        predefinedShots: [], // Initialize as an empty array
        customShotList: '',  // Initialize as an empty string
    });

    const sectionStyles = {
        backgroundColor: '#ffffff', // White background
        border: '1px solid #dcdcdc', // Light gray border
        borderRadius: '8px', // Rounded corners
        padding: '16px', // Internal padding
        marginBottom: '16px', // Space between sections
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', // Subtle shadow
    };

    const buildPreferredAddress = ({ street = '', city = '', stateShortCode = '' }) => {
        return [street, city, stateShortCode].filter((part) => part).join(', ');
    };

    const getLastZoom = () => {
        const savedZoom = localStorage.getItem('lastZoom');
        const zoom = parseFloat(savedZoom);
        return !isNaN(zoom) ? zoom : defaultZoom;
    };

    const handleBackToMissions = () => {
        if (isDirty) {
            const confirmLeave = window.confirm(
                'You have unsaved changes. Are you sure you want to leave this page?'
            );
            if (!confirmLeave) return;
        }
        navigate('/missions');
    };

    const handleCancel = () => {
        setIsDirty(false);
        setModifiedFields({});
        navigate('/missions');
    };

    const handleFieldChange = (fieldOrEvent, value) => {
        let fieldName;
        let fieldValue;

        if (typeof fieldOrEvent === 'string') {
            // Called with fieldName and value directly
            fieldName = fieldOrEvent;
            fieldValue = value;
        } else if (fieldOrEvent?.target) {
            // Called with an event object
            fieldName = fieldOrEvent.target.name;
            fieldValue = fieldOrEvent.target.value;
        } else {
            console.error('Invalid arguments passed to handleFieldChange');
            return;
        }

        // Coerce specific fields to their expected types
        if (['flightRadius', 'flightAltitude', 'missionLengthEst'].includes(fieldName)) {
            fieldValue = parseFloat(fieldValue);
        }

        setIsDirty(true); // Mark the form as dirty
        setMissionData((prevData) => ({
            ...prevData,
            [fieldName]: fieldValue, // Dynamically update the field by name
        }));
        setModifiedFields((prevFields) => ({
            ...prevFields,
            [fieldName]: {
                value: fieldValue,
                timestamp: new Date().toISOString(), // Include a timestamp
            },
        }));
    };

    const getFormattedDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return isNaN(date.getTime())
            ? 'Invalid Date'
            : `${date.toLocaleDateString()} \n${date.toLocaleTimeString()}`;
    };

    const handleSaveMission = async () => {
        setIsFormSubmitted(true); // Trigger validation

        // Validate subscriptionGuid
        if (!missionData?.subscriptionGuid) {
            setDialogMessage('A subscription is required to save this mission. Please assign a default organization.');
            setSubscriptionDialogOpen(true);
            return; // Prevent saving if validation fails
        }

        // Validate PersonnelSection
        const personnelErrors = personnelRef.current?.validate() || {};
        if (Object.keys(personnelErrors).length > 0) {
            console.error('Validation failed:', personnelErrors);
            return; // Prevent saving if validation fails
        }

        // Validate PostflightSection
        const postflightErrors = postflightRef.current?.validate() || {};
        if (Object.keys(postflightErrors).length > 0) {
            console.error('Validation failed:', postflightErrors);
            return; // Prevent saving if validation fails
        }

        if (!missionData?.missionName) {
            console.error('Mission name is required.');
            return; // Prevent saving if validation fails
        }

        try {
            if (missionGuid === 'new') {
                await createMission(missionData);
            } else {
                const payload = {
                    modifiedFields,
                    version: missionData.version, // Include version for concurrency control
                };
                await updateMission(missionGuid, payload);
            }
            setOriginalMissionData(missionData); // Save changes as original
            setIsDirty(false); // Reset dirty state
            navigate('/missions');
        } catch (error) {
            console.error('Error saving mission:', error);
        }
    };

    useEffect(() => {
        const loadSubscriptionData = async () => {
            if (selectedOrg) {
                try {
                    const subscriptionData = await getSubscriptionByGuid(selectedOrg);
                    if (subscriptionData) {
                        setSubscription(subscriptionData.subscription);
                    } else {
                        throw new Error('Subscription not found');
                    }
                } catch (error) {
                    console.error('Error loading subscription data:', error);
                    setError('Failed to load subscription data.');
                }
            } else {
                setSubscription(null); // Clear subscription if no subscriptionGuid exists
            }
        };

        loadSubscriptionData();
    }, [selectedOrg]);

    useEffect(() => {
        setLocationExpanded(!missionData?.completedAt);
        setGpsExpanded(!missionData?.completedAt);

    }, [missionData]);

    // Update or validate subscriptionGuid during initialization or on save
    /*
        useEffect(() => {
            if (selectedOrg && missionData?.subscriptionGuid !== selectedOrg) {
                setMissionData((prevData) => ({
                    ...prevData,
                    subscriptionGuid: selectedOrg,
                }));
                setModifiedFields((prevFields) => ({
                    ...prevFields,
                    subscriptionGuid: {
                        value: selectedOrg,
                        timestamp: new Date().toISOString(),
                    },
                }));
            }
        }, [selectedOrg, missionData?.subscriptionGuid]);
    */

    useEffect(() => {
        if (!isLoading && missionData && !missionData.subscriptionGuid) {
            setDialogMessage('This mission does not have a valid organization assigned. Please assign a default organization to continue.');
            setSubscriptionDialogOpen(true);
        }
    }, [isLoading, missionData]);

    // Initialize or fetch mission data
    useEffect(() => {
        if (!selectedOrg) {
            setError('No organization selected.');
            setIsLoading(false);
            return;
        }

        const fetchOrInitializeMission = async () => {
            if (missionGuid === 'new') {
                // Creating a new mission
                const newMission = {
                    missionGuid: uuidv4(),
                    subscriptionGuid: selectedOrg,
                    missionId: null,
                    userGuid: null,
                    jsonPlaceData: {
                        street: null,
                        city: null,
                        state: null,
                        country: null,
                        preferredAddress: '',
                    },
                    lat: null,
                    lon: null,
                    flightRadius: 1000,
                    flightAltitude: 120,
                    remotePic: '',
                    laancState: 'undefined',
                    airspaceClass: 'G',
                    missionName: null,
                    objectives: null,
                    locationDesc: null,
                    uavUuid: null,
                    jsonBatteryPerf: null,
                    maintNotes: null,
                    flightDebrief: null,
                    part107Compliance: true,
                    insuranceInfo: null,
                    weatherDataProvider: 'OpenWeatherMap',
                    jsonCameraSettings: null,
                    isLocked: false,
                    crew: null,
                    version: 1,
                    missionLengthEst: 30, // Default to 30 minutes
                    dateFlexibility: 0,
                    timeFlexibility: 0,
                    deletedAt: null,
                    scheduledAt: null,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                };
                setMissionData(newMission);
                setOriginalMissionData(newMission);
                setFetchedAddress(''); // Clear address for new missions
                setIsLoading(false);
            } else {
                // Fetch existing mission
                try {
                    const existingMission = await getMissionByGuid(missionGuid);
                    setMissionData(existingMission);
                    setOriginalMissionData(existingMission);
                    setFetchedAddress(existingMission?.jsonPlaceData?.preferredAddress || '');
                } catch (error) {
                    console.error('Error fetching mission:', error);
                } finally {
                    setIsLoading(false);
                }
            }
        };

        fetchOrInitializeMission();
    }, [missionGuid, selectedOrg]);

    // Compare missionData and originalMissionData to determine if the form is dirty
    useEffect(() => {
        if (!missionData || !originalMissionData) return;
        setIsDirty(JSON.stringify(missionData) !== JSON.stringify(originalMissionData));
    }, [missionData, originalMissionData]);

    /*
        useEffect(() => {
            if (mapRef.current && missionData?.lat && missionData?.lon && shouldCenterMap) {
                mapRef.current.easeTo({
                    center: [missionData.lon, missionData.lat],
                    zoom: getLastZoom(),
                    essential: true,
                });
                setShouldCenterMap(false); // Reset to avoid unintended movement
            }
        }, [missionData?.lat, missionData?.lon, shouldCenterMap]);
    */

    //useEffect(() => {
    //    console.log('mapContainerRef:', mapContainerRef.current);
    //}, []);

    // Initialize Mapbox map
    useEffect(() => {
        const initializeMap = () => {
            console.log('Initializing map...');
            if (!mapRef.current && mapContainerRef.current) {
                console.log('Map container found, initializing Mapbox...');

                // Retrieve last location from localStorage
                const lastLat = localStorage.getItem('lastLat');
                const lastLon = localStorage.getItem('lastLon');
                const savedZoom = localStorage.getItem('lastZoom');

                const initialCenter = lastLat && lastLon
                    ? [parseFloat(lastLon), parseFloat(lastLat)]
                    : defaultCenter;

                const initialZoom = savedZoom ? parseFloat(savedZoom) : defaultZoom;

                // Initialize the map only once
                const mapInstance = new mapboxgl.Map({
                    container: mapContainerRef.current,
                    style: 'mapbox://styles/mapbox/streets-v11',
                    center: initialCenter,
                    zoom: initialZoom,
                    antialias: true,
                });

                // Add zoom and rotation controls
                mapInstance.addControl(new mapboxgl.NavigationControl(), 'top-right');

                // Save the map instance to a ref for later use
                mapRef.current = mapInstance;

                // Add event listeners
                mapInstance.on('moveend', () => {
                    const center = mapInstance.getCenter();
                    const zoom = mapInstance.getZoom();
                    localStorage.setItem('lastLat', center.lat.toString());
                    localStorage.setItem('lastLon', center.lng.toString());
                    localStorage.setItem('lastZoom', zoom.toString());
                });

                mapInstance.on('click', async (event) => {
                    const { lng, lat } = event.lngLat;

                    // Update mission data with the clicked location
                    setMissionData((prevData) => ({
                        ...prevData,
                        lat,
                        lon: lng,
                    }));

                    try {
                        const addressData = await reverseGeocode(lng, lat);
                        handleFieldChange('lat', lat);
                        handleFieldChange('lon', lng);
                        handleFieldChange('jsonPlaceData', {
                            ...missionData?.jsonPlaceData,
                            ...addressData,
                        });
                        handleFieldChange('preferredAddress', addressData.preferredAddress);

                        setFetchedAddress(addressData.preferredAddress);

                        // Add marker
                        if (markerRef.current) {
                            markerRef.current.setLngLat([lng, lat]);
                        } else {
                            const marker = new mapboxgl.Marker().setLngLat([lng, lat]).addTo(mapInstance);
                            markerRef.current = marker; // Save marker instance
                        }
                    } catch (error) {
                        console.error('Error handling map click:', error);
                    }
                });

                setMap(mapInstance);
            }
        };

        initializeMap();
    }, [[mapContainerRef.current]]);

    useEffect(() => {
        if (mapRef.current && missionData?.lon && missionData?.lat) {
            // Center the map on the new location
            mapRef.current.easeTo({
                center: [missionData.lon, missionData.lat],
                zoom: getLastZoom(),
                essential: true,
            });

            // Update or create the marker
            if (markerRef.current) {
                markerRef.current.setLngLat([missionData.lon, missionData.lat]);
            } else {
                const marker = new mapboxgl.Marker()
                    .setLngLat([missionData.lon, missionData.lat])
                    .addTo(mapRef.current);
                markerRef.current = marker; // Save marker instance
            }
        }
    }, [missionData?.lon, missionData?.lat]);

    const handleChangeMapStyle = (styleUrl) => {
        if (mapRef.current) {
            mapRef.current.setStyle(styleUrl);
        } else {
            console.warn('Map instance is not initialized.');
        }
    };

    /*
    useEffect(() => {
        if (mapContainerRef.current) {
            mapContainerRef.current.innerHTML = ''; // Clear any existing content
        }

        const savedLat = localStorage.getItem('lastLat');
        const savedLon = localStorage.getItem('lastLon');
        const savedZoom = localStorage.getItem('lastZoom');

        const initialCenter = savedLat && savedLon ? [parseFloat(savedLon), parseFloat(savedLat)] : defaultCenter;
        const initialZoom = savedZoom ? parseFloat(savedZoom) : defaultZoom;

        if (mapContainerRef.current) {
            const mapInstance = new mapboxgl.Map({
                container: mapContainerRef.current,
                style: 'mapbox://styles/mapbox/streets-v11',
                center: initialCenter,
                zoom: initialZoom,
            });

            // Add zoom and rotation controls to the map
            mapInstance.addControl(new mapboxgl.NavigationControl(), 'top-right');

            // Add event listener to save location to localStorage on moveend
            mapInstance.on('moveend', () => {
                const center = mapInstance.getCenter();
                const zoom = mapInstance.getLastZoom();

                localStorage.setItem('lastLat', center.lat.toString());
                localStorage.setItem('lastLon', center.lng.toString());
                localStorage.setItem('lastZoom', zoom.toString());
            });

            // Add a marker for the mission location, if available
            if (missionData?.lon && missionData?.lat) {
                new mapboxgl.Marker()
                    .setLngLat([missionData.lon, missionData.lat])
                    .addTo(mapInstance);
            }

            setMap(mapInstance);
            mapRef.current = mapInstance;

            return () => {
                if (mapInstance) {
                    mapInstance.remove(); // Clean up the map instance
                }
            };
        }
    }, [missionData?.lon, missionData?.lat]);
*/

    /*
        useEffect(() => {
            const savedLat = localStorage.getItem('lastLat');
            const savedLon = localStorage.getItem('lastLon');
            const savedZoom = localStorage.getItem('lastZoom');
    
            const initialCenter =
                missionData?.lon && missionData?.lat
                    ? [missionData.lon, missionData.lat]
                    : savedLat && savedLon
                        ? [
                            parseFloat(savedLon),
                            parseFloat(savedLat),
                        ]
                        : defaultCenter;
    
            const initialZoom = savedZoom ? parseFloat(savedZoom) : defaultZoom;
    
            if (mapContainerRef.current) {
                //if (!mapContainerRef.current.hasChildNodes()) {
                const mapInstance = new mapboxgl.Map({
                    container: mapContainerRef.current,
                    style: 'mapbox://styles/mapbox/streets-v11',
                    center: initialCenter,
                    zoom: initialZoom,
                });
    
                // Add zoom and rotation controls to the map
                mapInstance.addControl(new mapboxgl.NavigationControl(), 'top-right');
    
                // Add event listener to save location to localStorage on moveend
                mapInstance.on('moveend', () => {
                    const center = mapInstance.getCenter();
                    const zoom = mapInstance.getZoom();
    
                    // Save location and zoom state if not triggered by a click
                    if (!mapInstance.isMoving()) {
                        localStorage.setItem('lastLat', center.lat.toString());
                        localStorage.setItem('lastLon', center.lng.toString());
                        localStorage.setItem('lastZoom', zoom.toString());
                    }
                });
    
                // Add event listener for map clicks
                mapInstance.on('click', async (event) => {
                    const { lng, lat } = event.lngLat;
    
                    // Update the mission data with the clicked coordinates
                    setMissionData((prevData) => ({
                        ...prevData,
                        lat: lat,
                        lon: lng,
                    }));
    
                    // Do not center the map automatically
                    setShouldCenterMap(false);
    
                    // Clear the search box
                    setSearchQuery('');
                    setFetchedAddress('');
                    setSuggestions([]); // Clear any suggestions displayed
    
                    // Reverse geocode the clicked location to get the address
                    try {
                        const addressData = await reverseGeocode(lng, lat);
    
                        // Use handleFieldChange to update relevant fields
                        handleFieldChange('lat', lat);
                        handleFieldChange('lon', lng);
                        handleFieldChange('jsonPlaceData', {
                            ...missionData?.jsonPlaceData,
                            ...addressData,
                        });
                        handleFieldChange('preferredAddress', addressData.preferredAddress);
    
                        setFetchedAddress(addressData.preferredAddress); // Update fetchedAddress for UI display
                    } catch (error) {
                        console.error('Error handling map click:', error);
                    }
                });
    
                // Add a marker for the mission location, if available
                if (missionData?.lon && missionData?.lat) {
                    new mapboxgl.Marker()
                        .setLngLat([missionData.lon, missionData.lat])
                        .addTo(mapInstance);
                }
    
               
    
                setMap(mapInstance);
                mapRef.current = mapInstance;
    
                return () => {
                    mapInstance.remove();
                };
                // }
            }
        }, [missionData?.lon, missionData?.lat]);
    */




    const handleCenterOnLocation = () => {
        if (!mapRef.current) {
            console.warn('Map instance is not initialized.');
            return;
        }

        setIsDeterminingLocation(true); // Start showing the progress indicator

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords;

                // Safely get the current zoom level or default to 10
                const currentZoom = getLastZoom();

                mapRef.current.jumpTo({
                    center: [longitude, latitude],
                    zoom: currentZoom, // Use current or saved zoom level
                    duration: 2000, // Smooth transition
                    easing: (t) => t * (2 - t), // Optional: Add easing for smoothness
                });

                // Save the updated location and zoom to sessionStorage
                sessionStorage.setItem('lastLat', latitude.toString());
                sessionStorage.setItem('lastLon', longitude.toString());
                sessionStorage.setItem('lastZoom', currentZoom.toString());

                setIsDeterminingLocation(false);
            },
            (error) => {
                console.error('Error fetching location:', error);
                alert('Unable to fetch your location. Please enable location services.');
                setIsDeterminingLocation(false);
            }
        );
    };

    const updateMissionLocation = (addressData, longitude, latitude) => {
        handleFieldChange('lat', latitude);
        handleFieldChange('lon', longitude);
        handleFieldChange('jsonPlaceData', {
            ...missionData?.jsonPlaceData,
            ...addressData,
        });
        handleFieldChange('preferredAddress', addressData.preferredAddress);

        setFetchedAddress(addressData.preferredAddress);

        if (mapRef.current) {
            mapRef.current.jumpTo({
                center: [longitude, latitude],
                zoom: getLastZoom(),
            });
        }
    };

    const handleAddressLookup = async () => {
        if (!searchQuery) return;

        setShouldCenterMap(true);

        const coordRegex = /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/;

        try {
            if (coordRegex.test(searchQuery)) {
                // Handle coordinate-based input
                const [latitude, longitude] = searchQuery.split(',').map((coord) => parseFloat(coord.trim()));

                if (latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180) {
                    const addressData = await reverseGeocode(longitude, latitude);
                    updateMissionLocation(addressData, longitude, latitude);
                } else {
                    alert('Invalid coordinates. Please enter valid latitude and longitude.');
                }
            }
            else {
                /*
                // Handle address-based input
                const response = await fetch(
                    `https://api.mapbox.com/geocoding/v6/mapbox.places/${encodeURIComponent(searchQuery)}.json?access_token=${mapboxgl.accessToken}`
                );
                const data = await response.json();

                if (data.features && data.features.length > 0) {
                    const { center } = data.features[0];
                    const [longitude, latitude] = center;

                    const addressData = await reverseGeocode(longitude, latitude); // Use reverseGeocode utility
                    updateMissionLocation(addressData, longitude, latitude);
                }
                */
            }
        } catch (error) {
            console.error('Error during address lookup:', error);
        }
    };

    const handleAddressInputChange = async (e) => {
        const query = e.target.value;
        setSearchQuery(query);

        /*
                // Reset fetchedAddress when the query is cleared or modified
                if (query.trim() === '' || query !== fetchedAddress) {
                    setFetchedAddress('');
                    setMissionData((prevData) => ({
                        ...prevData,
                        lat: null,
                        lon: null,
                        jsonPlaceData: {
                            ...prevData.jsonPlaceData,
                            preferredAddress: '',
                        },
                    }));
                }
        */

        if (query.length > 2) { // Fetch suggestions if query length is greater than 2
            try {
                const response = await fetch(
                    `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${mapboxgl.accessToken}&autocomplete=true&country=US,CA`
                );
                const data = await response.json();

                if (data.features && data.features.length > 0) {
                    setSuggestions(data.features); // Update suggestions state
                } else {
                    setSuggestions([]);
                }
            } catch (error) {
                console.error('Error fetching autocomplete suggestions:', error);
            }
        } else {
            setSuggestions([]); // Clear suggestions for shorter queries
        }
    };

    const handleSuggestionClick = async (suggestion) => {
        const [longitude, latitude] = suggestion.center;

        try {
            // Use reverseGeocode to fetch the address data
            const addressData = await reverseGeocode(longitude, latitude);

            // Use updateMissionLocation to update the mission data and map
            updateMissionLocation(addressData, longitude, latitude);

            // Update the search query and clear suggestions
            setSearchQuery(addressData.preferredAddress);
            setSuggestions([]);
        } catch (error) {
            console.error('Error handling suggestion click:', error);
        }
    };


    // Disable map movement on marker click
    /*    useEffect(() => {
            if (mapRef.current) {
                mapRef.current.on('moveend', () => {
                    // This ensures that clicking on the map doesn't unintentionally adjust the zoom/position
                    const center = mapRef.current.getCenter();
                    const zoom = mapRef.current.getZoom();
    
                    // Save position and zoom without overwriting when clicking
                    localStorage.setItem('lastLat', center.lat.toString());
                    localStorage.setItem('lastLon', center.lng.toString());
                    localStorage.setItem('lastZoom', zoom.toString());
                });
            }
        }, []);
    */

    if (isLoading) {
        console.log('isLoading');
        return (
            <Box
                sx={{
                    width: '100%',
                    padding: '16px',
                    backgroundColor: '#f9f9f9',
                }}
            >
                <LinearProgress
                    sx={{
                        height: 4,
                        borderRadius: 4,
                        backgroundColor: '#e0e0e0', // Track color
                        '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            backgroundColor: '#3f51b5', // Bar color
                        },
                    }}
                />
            </Box>
        );
    }

    return (
        <Box sx={{ px: 2, pb: 2 }}>
            <Dialog open={isSubscriptionDialogOpen} onClose={() => setSubscriptionDialogOpen(false)}>
                <DialogTitle>Assign Default Organization</DialogTitle>
                <DialogContent>
                    <Typography>{dialogMessage}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSubscriptionDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={() => {
                            if (selectedOrg) {
                                setMissionData((prevData) => ({
                                    ...prevData,
                                    subscriptionGuid: selectedOrg,
                                }));
                                setModifiedFields((prevFields) => ({
                                    ...prevFields,
                                    subscriptionGuid: {
                                        value: selectedOrg,
                                        timestamp: new Date().toISOString(),
                                    },
                                }));
                                setSubscriptionDialogOpen(false);
                            } else {
                                alert('No default organization is available. Please select an organization.');
                            }
                        }}
                        variant="contained"
                        color="primary"
                    >
                        Assign Default
                    </Button>
                </DialogActions>
            </Dialog>


            {/* Back to Missions Button */}
            <Button
                variant="text"
                onClick={handleBackToMissions}
                startIcon={<ArrowBackIcon />}
                sx={{
                    marginBottom: '16px',
                    color: '#2c3e50',
                    textTransform: 'none',
                    fontWeight: 400,
                    '&:hover': {
                        backgroundColor: '#f0f0f0',
                    },
                }}
            >
                Missions
            </Button>

            <Typography variant="h4" sx={{ marginBottom: '16px' }}>
                {missionGuid === 'new' ? 'Create New Mission' : 'Edit Mission'}
            </Typography>

            {/* Mission Name Input */}
            <TextField
                id="mission-name"
                label="Mission Name"
                name="missionName"
                value={missionData?.missionName || ''}
                onChange={handleFieldChange}
                fullWidth
                sx={{ marginBottom: '32px' }}
                autoComplete='off'
                slotProps={{ input: { maxLength: 255 } }}
                error={!missionData?.missionName}
            /*helperText={
                isFormSubmitted && !missionData?.missionName
                    ? 'Mission name is required.'
                    : missionData?.missionName
                        ? `${missionData.missionName.length}/255 characters used`
                        : ''
            }*/
            />

            {/* Post-flight Checklist Section */}
            {missionData?.completedAt && (
                <PostflightSection
                    ref={postflightRef}
                    missionData={missionData}
                    setMissionData={setMissionData}
                    setIsDirty={setIsDirty}
                    handleFieldChange={handleFieldChange}
                />
            )}

            {/* Shot List Section */}
            {hasCapability(subscription?.planId, CAPABILITIES.CUSTOMERS) ? (
                <>
                    <CustomerSection
                        missionData={missionData}
                        setMissionData={setMissionData}
                        setIsDirty={setIsDirty}
                        handleFieldChange={handleFieldChange}
                    />
                </>
            ) : null}

            {/* Assets Section */}
            {false && (
                <AssetsSection />
            )}

            {/* Location Section */}
            <Box sx={sectionStyles}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        marginBottom: isLocationExpanded ? '24px' : '0px',
                    }}
                    onClick={() => setLocationExpanded(!isLocationExpanded)}
                >
                    <LocationIcon sx={{ color: '#42a5f5', marginRight: '8px' }} />
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        Location
                    </Typography>
                    {isLocationExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Box>

                <Collapse in={isLocationExpanded}>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '24px',
                            flexWrap: 'nowrap', // Prevent wrapping
                            alignItems: 'flex-start', // Align items to the top
                        }}
                    >
                        <Box
                            sx={{ flex: 2 }}
                        >
                            {/* Map and Overlay Container */}
                            <Box
                                ref={mapContainerRef}
                                sx={{
                                    position: 'relative',
                                    width: '100%',
                                    height: '500px',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    zIndex: 0,
                                    border: '1px solid gray',
                                    marginBottom: '16px',
                                }}
                            >
                            </Box>

                            {/* Center on Location and Map Style Buttons */}
                            {/* Center on Location and Map Style Buttons */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column', // Stack vertically
                                    alignItems: 'flex-start', // Align items to the left
                                    gap: '16px', // Space between buttons
                                    marginTop: '8px',
                                    padding: '8px 0',
                                }}
                            >
                                {/* Linear Progress Indicator */}
                                {isDeterminingLocation && (
                                    <Box
                                        sx={{
                                            width: '100%',
                                        }}
                                    >
                                        <LinearProgress
                                            sx={{
                                                height: '4px',
                                                borderRadius: '2px',
                                                backgroundColor: '#e0e0e0',
                                                '& .MuiLinearProgress-bar': {
                                                    backgroundColor: '#3f51b5',
                                                },
                                            }}
                                        />
                                    </Box>
                                )}

                                {/* Buttons */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '16px', // Space between the "Center on Location" button and map style buttons
                                    }}
                                >
                                    {/* Center on Location Button */}
                                    <Button
                                        variant="contained"
                                        onClick={handleCenterOnLocation}
                                        disabled={isDeterminingLocation}
                                        sx={{
                                            backgroundColor: isDeterminingLocation ? '#f0f0f0' : '#ffffff',
                                            color: isDeterminingLocation ? '#c0c0c0' : '#2c3e50',
                                            '&:hover': {
                                                backgroundColor: '#e0e0e0',
                                            },
                                            borderRadius: '50%', // Circular button
                                            width: '36px',
                                            height: '36px',
                                            minWidth: 'auto',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                                        }}
                                    >
                                        <MyLocationIcon />
                                    </Button>

                                    {/* Map Style Buttons */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: '8px', // Space between map style buttons
                                        }}
                                    >
                                        <Button
                                            variant="outlined"
                                            onClick={() => handleChangeMapStyle('mapbox://styles/mapbox/streets-v11')}
                                            sx={{
                                                padding: '4px 8px',
                                                fontSize: '12px',
                                                textTransform: 'none',
                                            }}
                                        >
                                            Street
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            onClick={() => handleChangeMapStyle('mapbox://styles/mapbox/satellite-v9')}
                                            sx={{
                                                padding: '4px 8px',
                                                fontSize: '12px',
                                                textTransform: 'none',
                                            }}
                                        >
                                            Satellite
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            onClick={() => handleChangeMapStyle('mapbox://styles/mapbox/outdoors-v11')}
                                            sx={{
                                                padding: '4px 8px',
                                                fontSize: '12px',
                                                textTransform: 'none',
                                            }}
                                        >
                                            Outdoors
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>

                        {/* Input Fields */}
                        <Box
                            sx={{
                                flex: 1, // Take up 1/3 of the space
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '16px', // Space between input fields
                            }}
                        >

                            <Box>
                                {/* Address Search Box */}
                                <Box
                                    sx={{
                                        position: 'relative',
                                        marginBottom: '24px',
                                    }}
                                >
                                    {/* Address Search Box */}
                                    <TextField
                                        id="address-search"
                                        variant="outlined"
                                        value={searchQuery}
                                        onChange={handleAddressInputChange}
                                        onKeyDown={async (e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                await handleAddressLookup();
                                            }
                                        }}
                                        autoComplete="off"
                                        placeholder="Search for address or coordinates"
                                        slotProps={{
                                            input: {
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={async () => {
                                                                await handleAddressLookup();
                                                            }}
                                                        >
                                                            <SearchIcon />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            },
                                        }}
                                        fullWidth
                                        sx={{
                                            backgroundColor: '#ffffff',
                                            height: '40px',
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: '#ffffff',
                                                height: '40px',
                                                paddingRight: '8px',
                                            },
                                        }}
                                    />

                                    {/* Suggestions Dropdown */}
                                    {suggestions.length > 0 && (
                                        <Box
                                            sx={{
                                                position: 'absolute', // Position it directly below the search box
                                                top: '100%', // Place it below the TextField
                                                left: 0,
                                                right: 0,
                                                marginTop: '16px',
                                                maxHeight: '200px',
                                                overflowY: 'auto',
                                                backgroundColor: '#fff',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                                zIndex: 1200,
                                                boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)', // Add a subtle shadow
                                            }}
                                        >
                                            {suggestions.map((suggestion, index) => (
                                                <Box
                                                    key={index}
                                                    onClick={() => handleSuggestionClick(suggestion)}
                                                    sx={{
                                                        padding: '8px',
                                                        cursor: 'pointer',
                                                        '&:hover': { backgroundColor: '#f0f0f0' },
                                                    }}
                                                >
                                                    {suggestion.place_name}
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>

                            </Box>

                            {/* Fetched Address Display */}
                            {fetchedAddress && (
                                <Box sx={{ marginTop: '0px' }}>

                                    {/* Flight Radius Input */}
                                    <Box sx={{ display: 'flex', gap: '16px' }}>

                                        {/* Flight Radius Input */}
                                        <TextField
                                            id="flight-radius"
                                            name="flightRadius"
                                            label="Flight Radius (ft)"
                                            type="number"
                                            value={missionData?.flightRadius || ''}
                                            onChange={handleFieldChange}
                                            sx={{ flex: 1 }} // Allow inputs to scale evenly
                                        />

                                        {/* Flight Altitude Input */}
                                        <TextField
                                            id="flight-altitude"
                                            name="flightAltitude"
                                            label="Altitude MSL (ft)"
                                            type="number"
                                            value={missionData?.flightAltitude || ''}
                                            onChange={handleFieldChange}
                                            sx={{ flex: 1 }} // Allow inputs to scale evenly
                                        />
                                    </Box>

                                    {/* Latitude and Longitude */}
                                    {missionData?.lat && missionData?.lon && (
                                        <Box sx={{ marginTop: '32px' }}>
                                            {/* Address */}
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: '#6c757d',
                                                    fontWeight: 'bold',
                                                    display: 'block',
                                                    marginBottom: '0px',
                                                }}
                                            >
                                                Address:
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: '#6c757d',
                                                    wordBreak: 'break-word',
                                                }}
                                            >
                                                {fetchedAddress}
                                            </Typography>

                                            {/* Latitude and Longitude in a Row */}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    gap: '24px', // Space between latitude and longitude
                                                    marginTop: '16px',
                                                }}
                                            >
                                                {/* Latitude */}
                                                <Box>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: '#6c757d',
                                                            fontWeight: 'bold',
                                                            display: 'block',
                                                        }}
                                                    >
                                                        Latitude:
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: '#6c757d',
                                                        }}
                                                    >
                                                        {missionData.lat.toFixed(6) || 'N/A'}
                                                    </Typography>
                                                </Box>

                                                {/* Longitude */}
                                                <Box>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: '#6c757d',
                                                            fontWeight: 'bold',
                                                            display: 'block',
                                                        }}
                                                    >
                                                        Longitude:
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: '#6c757d',
                                                        }}
                                                    >
                                                        {missionData.lon.toFixed(6) || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {/* AirHub Portal Link */}
                                            <Box sx={{ marginTop: '32px' }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: '#6c757d',
                                                    }}
                                                >
                                                    Check airspace at a {' '}
                                                    <Typography
                                                        component="span"
                                                        variant="body2"
                                                        sx={{
                                                            color: '#1976d2',
                                                            textDecoration: 'underline',
                                                            cursor: 'pointer',
                                                            '&:hover': {
                                                                color: '#1565c0', // Slightly darker blue on hover
                                                            },
                                                        }}
                                                        onClick={() => window.open('https://www.faa.gov/uas/getting_started/laanc#approved', '_blank')}
                                                    >
                                                        LAANC service provider
                                                    </Typography>
                                                </Typography>
                                            </Box>

                                            {/* Airspace Class and LAANC State */}
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '1fr 1fr', // Two equal columns
                                                    gap: '16px', // Space between the two dropdowns
                                                    marginTop: '24px',
                                                }}
                                            >
                                                {/* Airspace Class Dropdown */}
                                                <Box>
                                                    <TextField
                                                        select
                                                        id="airspace-class"
                                                        name="airspaceClass"
                                                        label="Airspace Class"
                                                        value={missionData?.airspaceClass || ''}
                                                        onChange={handleFieldChange}
                                                        sx={{
                                                            minWidth: '180px',
                                                            width: '100%',
                                                        }}
                                                    >
                                                        <MenuItem value="A">Class A</MenuItem>
                                                        <MenuItem value="B">Class B</MenuItem>
                                                        <MenuItem value="C">Class C</MenuItem>
                                                        <MenuItem value="D">Class D</MenuItem>
                                                        <MenuItem value="E">Class E</MenuItem>
                                                        <MenuItem value="G">Class G</MenuItem>
                                                    </TextField>

                                                    {/* Helper Text for Airspace Class */}
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            display: 'none',
                                                            marginTop: '8px',
                                                            color: '#6c757d',
                                                        }}
                                                    >
                                                        {(() => {
                                                            switch (missionData?.airspaceClass) {
                                                                case 'A':
                                                                    return 'High-altitude airspace, typically above 18,000 feet.';
                                                                case 'B':
                                                                    return 'Busy airport areas with strict control.';
                                                                case 'C':
                                                                    return 'Medium-sized airports, controlled airspace.';
                                                                case 'D':
                                                                    return 'Smaller airports with control towers.';
                                                                case 'E':
                                                                    return 'General controlled airspace not otherwise specified.';
                                                                case 'G':
                                                                    return 'Uncontrolled airspace.';
                                                                default:
                                                                    return 'Select an airspace class to see its description.';
                                                            }
                                                        })()}
                                                    </Typography>

                                                </Box>

                                                {/* LAANC State Dropdown */}
                                                <TextField
                                                    select
                                                    id="laanc-state"
                                                    name="laancState"
                                                    label="LAANC State"
                                                    value={missionData?.laancState || 'undefined'}
                                                    onChange={handleFieldChange}
                                                    sx={{
                                                        minWidth: '180px',
                                                        width: '100%',
                                                    }}
                                                >
                                                    <MenuItem value="undefined">Undefined</MenuItem>
                                                    <MenuItem value="notNeeded">Not Needed</MenuItem>
                                                    <MenuItem value="pending">Pending</MenuItem>
                                                    <MenuItem value="approved">Approved</MenuItem>
                                                    <MenuItem value="rejected">Rejected</MenuItem>
                                                </TextField>

                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Collapse>
            </Box >

            {/* Personnel Section */}
            < Box
                sx={{
                    width: '100%',
                }}
            >
                <PersonnelSection
                    ref={personnelRef}
                    missionData={missionData}
                    setMissionData={setMissionData}
                    setIsDirty={setIsDirty}
                    handleFieldChange={handleFieldChange}
                />
            </Box >

            {/* Schedule Section */}
            < Box
                sx={{
                    width: '100%',
                }}
            >
                <ScheduleSection
                    missionData={missionData}
                    setMissionData={setMissionData}
                    originalMissionData={originalMissionData}
                    setIsDirty={setIsDirty}
                    handleFieldChange={handleFieldChange}
                />
            </Box >

            {/* Mission Details Header */}
            <DetailsSection
                missionData={missionData}
                setMissionData={setMissionData}
                setIsDirty={setIsDirty}
                handleFieldChange={handleFieldChange}
            />

            {/* Shot List Section */}
            {hasCapability(subscription?.planId, CAPABILITIES.POST_FLIGHT) ? (
                <>
                    <ShotListSection
                        missionData={missionData}
                        setMissionData={setMissionData}
                        setIsDirty={setIsDirty}
                        handleFieldChange={handleFieldChange}
                    />
                </>
            ) : null}

            {/* Pre-flight Checklist Section */}
            <PreflightSection
                missionData={missionData}
                setMissionData={setMissionData}
                setIsDirty={setIsDirty}
                handleFieldChange={handleFieldChange}
            />

            {/* GPS Coverage Check Section */}
            {false && (
                < Box sx={sectionStyles} >
                    {/* Section Header */}
                    < Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            marginBottom: isGpsExpanded ? '0px' : '0px',
                        }}
                        onClick={() => setGpsExpanded(!isGpsExpanded)}
                    >
                        <SatelliteIcon sx={{ color: '#42a5f5', marginRight: '8px' }} />
                        <Typography variant="h6" sx={{ flexGrow: 1 }}>
                            GPS Coverage Check
                        </Typography>
                        {isGpsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Box >

                    {/* Expandable Content */}
                    < Collapse in={isGpsExpanded}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
                            <Typography variant="body2" sx={{ color: '#6c757d', paddingLeft: '32px' }}>
                                Ensure optimal GPS coverage before the mission.
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#6c757d', paddingLeft: '32px' }}>
                                Check the satellite visibility and strength in the mission area.
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                sx={{
                                    marginTop: '8px',
                                    textTransform: 'none',
                                    fontSize: '14px',
                                    marginLeft: '32px',
                                    width: 'fit-content',
                                    alignSelf: 'flex-start',
                                }}
                            >
                                Check Coverage
                            </Button>
                        </Box>
                    </Collapse >
                </Box >
            )}

            <Box sx={{ paddingBottom: '48px' }}></Box>

            {/* Pinned bar */}
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: `${CONFIG.MEASURE.SIDEBAR_WIDTH}px`,
                    right: 0,
                    display: 'flex',
                    //justifyContent: 'flex-end',
                    justifyContent: 'space-between', // Space between the text and buttons
                    alignItems: 'center',
                    //gap: 4,
                    padding: '8px',
                    backgroundColor: isDirty ? '#ffec9e' : '#f9f9f9',
                    boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
                    borderTop: '1px solid #dcdcdc',
                    //height: '48px',
                    zIndex: 999,
                }}
            >
                {/* Dirty state message or placeholder */}
                <Typography
                    variant="body2"
                    sx={{
                        color: isDirty ? '#6c757d' : 'transparent', // Hide text when not dirty
                        fontWeight: 400,
                        marginLeft: '16px', // Maintain alignment
                    }}
                >
                    Document modified.
                </Typography>

                {/* Save and Cancel Buttons */}
                <Box sx={{ display: 'flex', gap: 1, marginRight: '20px' }}>
                    <Button variant="outlined" onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveMission}
                        disabled={!isDirty} // Disable save button if no changes.
                    >
                        Save
                    </Button>
                </Box>
            </Box>
        </Box >

    );
};

export default MissionPlanner;